import { useCallback, useMemo, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import useMountEffect from '../useMountEffect';
import { useSharedTFunction } from '@shared/i18n';
import { trackEvent } from '@shared/utils';
import { ChatbotAuthStatus, ChatbotMessage, ChatbotSessionStatus, ChatbotUser } from './types';

interface Props {
  audience: string;
  url: string;
  userName: string;
  app: 'kjedeadmin' | 'mgpris';
  userGroup: string;
  answerNotFoundText?: any[];
}

interface UseChatBot {
  messages: ChatbotMessage[];
  sendMessage: (text: string) => void;
  authStatus: ChatbotAuthStatus;
  sessionStatus: ChatbotSessionStatus;
}

export function useChatBot({
  audience,
  url,
  userName,
  app,
  userGroup,
  answerNotFoundText,
}: Props): UseChatBot {
  const {
    getAccessTokenSilently,
    loginWithRedirect,
    isLoading: isLoadingAuth0,
    error,
  } = useAuth0();
  const [authToken, setAuthToken] = useState<string>();
  const t = useSharedTFunction();
  const [isLoadingAnswer, setIsLoadingAnswer] = useState(false);
  const [sessionStatus, setSessionStatus] = useState<ChatbotSessionStatus>('initiating');

  const [messages, setMessages] = useState<ChatbotMessage[]>([]);

  const authStatus: ChatbotAuthStatus = useMemo(() => {
    if (isLoadingAuth0) return 'initiating';
    if (error) return 'error';
    if (isLoadingAnswer) return 'loadingAnswer';
    return 'ready';
  }, [error, isLoadingAnswer, isLoadingAuth0]);

  const BOT: ChatbotUser = useMemo(() => ({ type: 'bot' }), []);
  const USER: ChatbotUser = useMemo(() => ({ type: 'user', name: userName }), [userName]);

  const createSession = useCallback(
    async (token: string) => {
      const sessionCreationErrorMessage: ChatbotMessage = {
        user: BOT,
        text: t('shared.chatbot.errors.failedToFetch'),
        type: 'text',
      };
      try {
        const response = await fetch(`${url}/chatbot/session`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
          },
          credentials: 'include',
        });

        if (!response.ok) {
          console.error('Failed to create session:', response.status);
          setSessionStatus('error');
          setMessages([sessionCreationErrorMessage]);
          return;
        } else {
          setSessionStatus('ready');
          trackEvent('chatbot_session_started', { user_group: userGroup });
          setMessages([
            {
              user: BOT,
              text: t('shared.chatbot.greeting', { appName: t(`shared.chatbot.apps.${app}`) }),
              type: 'text',
            },
          ]);
        }
      } catch (error) {
        setSessionStatus('error');
        setMessages([sessionCreationErrorMessage]);
        console.error('Session creation error:', error);
      }
    },
    [BOT, app, t, url, userGroup]
  );

  useMountEffect(() => {
    getAccessTokenSilently({
      authorizationParams: { audience },
    })
      .then((token) => {
        setAuthToken(token);
        createSession(token);
      })
      .catch((error) => {
        if (error?.error === 'login_required' || error?.error === 'consent_required') {
          loginWithRedirect({
            appState: { returnTo: window.location.pathname },
            authorizationParams: { audience },
          });
        } else {
          const errorMessage: ChatbotMessage = {
            type: 'text',
            user: BOT,
            text: t('shared.chatbot.errors.notAuthenticated'),
          };
          setMessages((prev) => [...prev, errorMessage]);
          console.error('Failed to get access token:', error?.error);
        }
      });
  });

  const sendMessage = useCallback(
    async (text: string) => {
      const userMessage: ChatbotMessage = { user: USER, text, type: 'text' };
      setMessages((prev) => [...prev, userMessage]);

      if (!authToken) {
        console.error('No auth token available');
        const errorMessage: ChatbotMessage = {
          user: BOT,
          text: t('shared.chatbot.errors.notAuthenticated'),
          type: 'text',
        };
        setMessages((prev) => [...prev, errorMessage]);
        return;
      }
      setIsLoadingAnswer(true);
      setMessages((prev) => [...prev, { type: 'loading', user: BOT }]);
      trackEvent('chatbot_message_sent', { user_group: userGroup });

      try {
        const response = await fetch(`${url}/chatbot`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${authToken}`,
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({ message: text, app, userGroup }),
        });

        if (!response.ok) {
          console.error('Response not ok:', response.status, response.statusText);
          const errorMessage: ChatbotMessage = {
            user: BOT,
            text: t('shared.chatbot.errors.httpError', {
              status: `${response.status} ${response.statusText}`,
            }),
            type: 'text',
          };
          setMessages((prev) => [...prev, errorMessage]);
          return;
        }

        const reader = response.body?.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        if (reader) {
          // eslint-disable-next-line no-constant-condition
          while (true) {
            const { done, value } = await reader.read();
            if (done) {
              break;
            }

            const chunk = decoder.decode(value);

            buffer += chunk;

            const lines = buffer.split('\n');
            buffer = lines.pop() || '';

            for (const line of lines) {
              if (line.trim()) {
                try {
                  const data = JSON.parse(line.trim());

                  if (data.content?.parts) {
                    for (const part of data.content.parts) {
                      if (part.text) {
                        const text = part.text;

                        if (
                          data.author === 'final_answer_nb' ||
                          data.author === 'final_answer_sv' ||
                          data.author === 'final_answer_en'
                        ) {
                          const botMessage: ChatbotMessage = {
                            user: BOT,
                            text: text,
                            type: 'text',
                          };
                          setMessages((prev) => [...prev, botMessage]);
                          return;
                        }

                        if (
                          data.author === 'answer_not_found_nb' ||
                          data.author === 'answer_not_found_sv' ||
                          data.author === 'answer_not_found_en'
                        ) {
                          const botMessage: ChatbotMessage =
                            answerNotFoundText && answerNotFoundText.length > 0
                              ? {
                                  user: BOT,
                                  content: answerNotFoundText,
                                  type: 'sanity',
                                }
                              : {
                                  user: BOT,
                                  text: text,
                                  type: 'text',
                                };
                          setMessages((prev) => [...prev, botMessage]);
                          return;
                        }
                      }
                    }
                  }
                } catch (parseError) {
                  console.warn('Failed to parse JSON line:', line, parseError);
                }
              }
            }
          }
        } else {
          console.error('No response body reader available');
        }
        const errorMessage: ChatbotMessage = {
          user: BOT,
          text: t('shared.chatbot.errors.noResponse'),
          type: 'text',
        };
        setMessages((prev) => [...prev.slice(0, -1), errorMessage]);
      } catch (error) {
        setIsLoadingAnswer(false);
        console.error('Chatbot error:', error);
        const errorText =
          error instanceof Error
            ? t('shared.chatbot.errors.failedToFetch')
            : t('shared.chatbot.errors.unknown');

        const errorMessage: ChatbotMessage = {
          user: BOT,
          text: errorText,
          type: 'text',
        };
        setMessages((prev) => [...prev, errorMessage]);
      } finally {
        setIsLoadingAnswer(false);
        setMessages((prev) => prev.filter((message) => message.type !== 'loading'));
      }
    },
    [USER, authToken, BOT, t, url, app, userGroup, answerNotFoundText]
  );

  return useMemo(
    () => ({ messages, sendMessage, authStatus, sessionStatus }),
    [messages, sendMessage, sessionStatus, authStatus]
  );
}
